import React, { useState, useRef, useCallback, useEffect } from 'react';
import Tooltip from '@mui/material/Tooltip';
import styles from './AiAgentLogo.module.css';

// const WEBHOOK_URL = 'https://arslansingel.app.n8n.cloud/webhook-test/extract-logo';
const WEBHOOK_URL = 'https://singlecombo-admin.app.n8n.cloud/webhook/extract-logo';

const PROCESSING_STEPS = [
    { id: 'upload',   icon: '📤', label: 'Uploading image'           },
    { id: 'analyze',  icon: '🔍', label: 'Analysing visual content'  },
    { id: 'extract',  icon: '🧠', label: 'AI extracting logo region' },
    { id: 'refine',   icon: '✨', label: 'Refining & isolating logo' },
    { id: 'export',   icon: '📦', label: 'Preparing result'          },
];

const STEP_TEXTS = [
    'Scanning image for brand elements…',
    'AI is identifying logo boundaries…',
    'Isolating logo from background…',
    'Enhancing logo clarity…',
    'Finalising your logo…',
];

function formatBytes(bytes) {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function isValidImage(file) {
    if (!file) return false;
    return file.type.startsWith('image/');
}

/** Try to derive a displayable image URL from various n8n response shapes (JSON / text). */
function extractLogoUrl(responseText) {
    try {
        const json = JSON.parse(responseText);
        const candidates = [
            json?.url, json?.logo_url, json?.logoUrl, json?.image_url,
            json?.imageUrl, json?.data?.url, json?.data?.logo_url,
            json?.result?.url, json?.output,
        ];
        for (const c of candidates) {
            if (typeof c === 'string' && c.trim()) return { type: 'url', value: c.trim() };
        }
        const b64Candidates = [
            json?.base64, json?.image, json?.logo, json?.data?.base64, json?.data?.image,
        ];
        for (const c of b64Candidates) {
            if (typeof c === 'string' && c.trim().length > 100) {
                const src = c.startsWith('data:') ? c : `data:image/png;base64,${c}`;
                return { type: 'base64', value: src };
            }
        }
    } catch (_) {
        const trimmed = responseText.trim();
        if (trimmed.startsWith('data:image')) return { type: 'base64', value: trimmed };
        if (trimmed.startsWith('http')) return { type: 'url', value: trimmed };
        if (/^[A-Za-z0-9+/=]{100,}$/.test(trimmed.replace(/\s/g, ''))) {
            return { type: 'base64', value: `data:image/png;base64,${trimmed.replace(/\s/g, '')}` };
        }
    }
    return null;
}

/** Detect image format from magic bytes (octet-stream / missing Content-Type). */
async function sniffImageMime(blob) {
    const slice = blob.slice(0, 32);
    const buf = new Uint8Array(await slice.arrayBuffer());
    if (buf.length >= 8 && buf[0] === 0x89 && buf[1] === 0x50 && buf[2] === 0x4E && buf[3] === 0x47) return 'image/png';
    if (buf.length >= 3 && buf[0] === 0xFF && buf[1] === 0xD8 && buf[2] === 0xFF) return 'image/jpeg';
    if (buf.length >= 6 && buf[0] === 0x47 && buf[1] === 0x49 && buf[2] === 0x46) return 'image/gif';
    if (buf.length >= 12 && buf[0] === 0x52 && buf[1] === 0x49 && buf[2] === 0x46 && buf[8] === 0x57 && buf[9] === 0x45 && buf[10] === 0x42 && buf[11] === 0x50) {
        return 'image/webp';
    }
    return null;
}

function mimeToExtension(mime) {
    if (!mime) return 'png';
    const m = mime.toLowerCase();
    if (m.includes('jpeg') || m.includes('jpg')) return 'jpg';
    if (m.includes('webp')) return 'webp';
    if (m.includes('gif')) return 'gif';
    if (m.includes('png')) return 'png';
    return 'png';
}

/**
 * n8n often returns raw image bytes with Content-Type: image/* or application/octet-stream.
 */
async function parseExtractResponse(response) {
    const blob = await response.blob();
    const headerCt = (response.headers.get('content-type') || '').split(';')[0].trim().toLowerCase();
    const blobType = (blob.type || '').split(';')[0].trim().toLowerCase();

    const isJsonHeader =
        headerCt.includes('application/json') ||
        headerCt === 'text/json';

    if (isJsonHeader && blob.size > 0) {
        const text = await blob.text();
        const extracted = extractLogoUrl(text);
        if (extracted) return { kind: 'parsed', result: extracted };
        return { kind: 'raw', result: { type: 'raw', value: text } };
    }

    const clearlyImage =
        headerCt.startsWith('image/') ||
        blobType.startsWith('image/') ||
        headerCt === 'application/octet-stream' ||
        headerCt === 'binary' ||
        headerCt === 'application/x-binary';

    if (clearlyImage && blob.size > 0) {
        let mime = headerCt.startsWith('image/') ? headerCt : blobType.startsWith('image/') ? blobType : null;
        if (!mime || mime === 'application/octet-stream' || mime === 'binary') {
            mime = (await sniffImageMime(blob)) || 'image/png';
        }
        const typedBlob = new Blob([blob], { type: mime });
        const objectUrl = URL.createObjectURL(typedBlob);
        return {
            kind: 'blob',
            result: {
                type: 'blob',
                objectUrl,
                mimeType: mime,
                extension: mimeToExtension(mime),
            },
        };
    }

    // Unknown / missing Content-Type: peek for JSON vs binary image
    if (blob.size > 0) {
        const head = await blob.slice(0, 8).text();
        const t = head.trimStart();
        if (t.startsWith('{') || t.startsWith('[')) {
            const text = await blob.text();
            const extracted = extractLogoUrl(text);
            if (extracted) return { kind: 'parsed', result: extracted };
            return { kind: 'raw', result: { type: 'raw', value: text } };
        }
        const sniffed = await sniffImageMime(blob);
        if (sniffed) {
            const typedBlob = new Blob([blob], { type: sniffed });
            const objectUrl = URL.createObjectURL(typedBlob);
            return {
                kind: 'blob',
                result: {
                    type: 'blob',
                    objectUrl,
                    mimeType: sniffed,
                    extension: mimeToExtension(sniffed),
                },
            };
        }
    }

    const text = await blob.text();
    const extracted = extractLogoUrl(text);
    if (extracted) return { kind: 'parsed', result: extracted };
    return { kind: 'raw', result: { type: 'raw', value: text } };
}

const AiAgentLogo = () => {
    const [file, setFile] = useState(null);
    const [previewSrc, setPreviewSrc] = useState('');
    const [isDragOver, setIsDragOver] = useState(false);
    const [status, setStatus] = useState('idle'); // idle | processing | done | error
    const [activeStep, setActiveStep] = useState(0);
    const [stepText, setStepText] = useState(STEP_TEXTS[0]);
    const [logoResult, setLogoResult] = useState(null); // { type, value } | { type: 'blob', objectUrl, ... }
    const [errorMsg, setErrorMsg] = useState('');

    const fileInputRef = useRef(null);
    const stepTimerRef = useRef(null);
    const textTimerRef = useRef(null);
    /** Revoke when replacing result or unmounting — blob URLs from the webhook response */
    const logoBlobUrlRef = useRef(null);

    const revokeLogoBlobUrl = useCallback(() => {
        if (logoBlobUrlRef.current) {
            URL.revokeObjectURL(logoBlobUrlRef.current);
            logoBlobUrlRef.current = null;
        }
    }, []);

    const pickFile = useCallback((f) => {
        if (!f) return;
        if (!isValidImage(f)) {
            setErrorMsg('Only image files are supported (PNG, JPG, WEBP, GIF, etc.).');
            return;
        }
        setErrorMsg('');
        setFile(f);
        setPreviewSrc(URL.createObjectURL(f));
        setStatus('idle');
        revokeLogoBlobUrl();
        setLogoResult(null);
    }, [revokeLogoBlobUrl]);

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        setIsDragOver(false);
        pickFile(e.dataTransfer.files?.[0] || null);
    }, [pickFile]);

    const removeFile = useCallback((e) => {
        e.stopPropagation();
        setFile(null);
        setPreviewSrc('');
        setStatus('idle');
        revokeLogoBlobUrl();
        setLogoResult(null);
        setErrorMsg('');
        if (fileInputRef.current) fileInputRef.current.value = '';
    }, [revokeLogoBlobUrl]);

    // Cleanup preview URL on unmount / file change
    useEffect(() => {
        return () => {
            if (previewSrc.startsWith('blob:')) URL.revokeObjectURL(previewSrc);
        };
    }, [previewSrc]);

    useEffect(() => () => {
        if (logoBlobUrlRef.current) {
            URL.revokeObjectURL(logoBlobUrlRef.current);
            logoBlobUrlRef.current = null;
        }
    }, []);

    // Animate steps while processing
    useEffect(() => {
        if (status !== 'processing') {
            clearInterval(stepTimerRef.current);
            clearInterval(textTimerRef.current);
            return;
        }

        setActiveStep(0);
        setStepText(STEP_TEXTS[0]);

        let stepIdx = 0;
        let textIdx = 0;

        stepTimerRef.current = setInterval(() => {
            stepIdx = Math.min(stepIdx + 1, PROCESSING_STEPS.length - 1);
            setActiveStep(stepIdx);
        }, 1800);

        textTimerRef.current = setInterval(() => {
            textIdx = (textIdx + 1) % STEP_TEXTS.length;
            setStepText(STEP_TEXTS[textIdx]);
        }, 2200);

        return () => {
            clearInterval(stepTimerRef.current);
            clearInterval(textTimerRef.current);
        };
    }, [status]);

    const handleExtract = useCallback(async () => {
        if (!file) return;
        setStatus('processing');
        setErrorMsg('');
        revokeLogoBlobUrl();
        setLogoResult(null);

        try {
            const response = await fetch(WEBHOOK_URL, {
                method: 'POST',
                headers: { 'Content-Type': file.type || 'application/octet-stream' },
                body: file,
            });

            if (!response.ok) {
                const errBlob = await response.blob();
                const errText = await errBlob.text();
                throw new Error(`Server responded with ${response.status}: ${errText.slice(0, 300)}`);
            }

            const parsed = await parseExtractResponse(response);
            const result = parsed.result;

            if (result.type === 'blob') {
                logoBlobUrlRef.current = result.objectUrl;
            }

            setLogoResult(result);
            setStatus('done');
        } catch (err) {
            setErrorMsg(err?.message || 'Something went wrong. Please try again.');
            setStatus('error');
        }
    }, [file, revokeLogoBlobUrl]);

    const handleReset = useCallback(() => {
        setFile(null);
        setPreviewSrc('');
        setStatus('idle');
        revokeLogoBlobUrl();
        setLogoResult(null);
        setErrorMsg('');
        if (fileInputRef.current) fileInputRef.current.value = '';
    }, [revokeLogoBlobUrl]);

    const isProcessing = status === 'processing';
    const isDone = status === 'done';
    const isError = status === 'error';
    const isDevelopmentMode = process.env.NODE_ENV === 'development';
    const isExtractDisabled = isDevelopmentMode || !file || isProcessing;

    return (
        <div className={styles.page}>
            {/* ── Hero ──────────────────────────────── */}
            <div className={styles.hero}>
                <div className={styles.heroBadge}>
                    <span className={styles.heroBadgeDot} />
                    AI Logo Extractor · Powered by n8n
                </div>
                <h1 className={styles.heroTitle}>
                    Extract Your <span>Brand Logo</span>
                </h1>
                <p className={styles.heroSub}>
                    Upload any image — our AI will detect, isolate, and extract the logo for you in seconds.
                </p>
            </div>

            {/* ── Upload / processing card ──────────── */}
            {!isProcessing ? (
                <div className={styles.card}>
                    {/* Drop zone */}
                    <div className={styles.dropZoneWrap}>
                        <span className={styles.dropZoneLabel}>Source Image</span>
                        <div
                            className={[
                                styles.dropZone,
                                file ? styles.dropZoneCompact : '',
                                isDragOver ? styles.dropZoneActive : '',
                                isProcessing ? styles.dropZoneDisabled : '',
                            ].filter(Boolean).join(' ')}
                            role='button'
                            tabIndex={0}
                            onClick={() => !isProcessing && fileInputRef.current?.click()}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click();
                            }}
                            onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
                            onDragLeave={() => setIsDragOver(false)}
                            onDrop={handleDrop}
                        >
                            <input
                                ref={fileInputRef}
                                type='file'
                                accept='image/*'
                                className={styles.hiddenInput}
                                onChange={(e) => pickFile(e.target.files?.[0] || null)}
                            />
                            {file ? (
                                <div className={styles.previewWrap}>
                                    <img src={previewSrc} alt='preview' className={styles.previewThumb} />
                                    <div className={styles.previewInfo}>
                                        <p className={styles.previewName}>{file.name}</p>
                                        <p className={styles.previewSize}>{formatBytes(file.size)}</p>
                                    </div>
                                    <button
                                        type='button'
                                        className={styles.previewRemove}
                                        aria-label='Remove image'
                                        onClick={removeFile}
                                    >
                                        ✕
                                    </button>
                                </div>
                            ) : (
                                <div className={styles.dropPrompt}>
                                    <div className={styles.dropIconWrap}>🖼️</div>
                                    <p className={styles.dropTitle}>Drop your image here</p>
                                    <p className={styles.dropHint}>PNG, JPG, WEBP, GIF · any size</p>
                                    <button type='button' className={styles.dropBrowseBtn} onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}>
                                        Browse files
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Error */}
                    {(errorMsg || isError) && (
                        <div className={styles.errorBanner}>
                            <span className={styles.errorBannerIcon}>⚠️</span>
                            <span>{errorMsg || 'Something went wrong. Please try again.'}</span>
                        </div>
                    )}

                    <div className={errorMsg || isError ? styles.cardCtaAfterError : styles.cardCta}>
                        <Tooltip
                            title='This feature is only available in production.'
                            placement='top'
                            arrow
                            disableHoverListener={!isDevelopmentMode}
                        >
                            <span className={styles.submitBtnWrap}>
                                <button
                                    type='button'
                                    className={styles.submitBtn}
                                    disabled={isExtractDisabled}
                                    onClick={handleExtract}
                                >
                                    <span className={styles.submitBtnIcon} aria-hidden>
                                        <svg width='20' height='20' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
                                            <circle cx='11' cy='11' r='7' stroke='currentColor' strokeWidth='2' strokeLinecap='round' />
                                            <path d='M20 20 16.65 16.65' stroke='currentColor' strokeWidth='2' strokeLinecap='round' />
                                        </svg>
                                    </span>
                                    Extract Logo with AI
                                </button>
                            </span>
                        </Tooltip>
                    </div>
                </div>
            ) : (
                /* ── AI Processing overlay ─────────── */
                <div className={styles.processingOverlay}>
                    <div className={styles.orb}>
                        <span className={styles.orbRing} />
                        <span className={styles.orbRing} />
                        <span className={styles.orbRing} />
                        <div className={styles.orbCore}>🤖</div>
                    </div>

                    <p className={styles.processingTitle}>AI is Working…</p>
                    <p className={styles.processingStepText}>{stepText}</p>

                    <div className={styles.dotsRow}>
                        {[0, 1, 2, 3, 4].map((i) => (
                            <span key={i} className={styles.dot} />
                        ))}
                    </div>

                    <div className={styles.stepPipeline}>
                        {PROCESSING_STEPS.map((step, idx) => {
                            const isDone = idx < activeStep;
                            const isActive = idx === activeStep;
                            return (
                                <React.Fragment key={step.id}>
                                    <div
                                        className={[
                                            styles.stepRow,
                                            isDone ? styles.stepRowDone : isActive ? styles.stepRowActive : styles.stepRowPending,
                                        ].join(' ')}
                                    >
                                        <div
                                            className={[
                                                styles.stepIcon,
                                                isDone ? styles.stepIconDone : isActive ? styles.stepIconActive : '',
                                            ].join(' ')}
                                        >
                                            {isDone ? '✅' : step.icon}
                                        </div>
                                        <span
                                            className={[
                                                styles.stepLabel,
                                                isDone ? styles.stepLabelDone : isActive ? styles.stepLabelActive : '',
                                            ].join(' ')}
                                        >
                                            {step.label}
                                        </span>
                                        {isDone && <span className={styles.stepCheck}>✓</span>}
                                    </div>
                                    {idx < PROCESSING_STEPS.length - 1 && (
                                        <div className={[styles.stepConnector, isDone ? styles.stepConnectorDone : ''].join(' ')} />
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* ── Result ───────────────────────────── */}
            {isDone && logoResult && (
                <div className={styles.resultSection}>
                    <div className={styles.resultHeader}>
                        <p className={styles.resultTitle}>
                            🎯 Extracted Logo
                        </p>
                        <span className={styles.resultBadge}>
                            ✓ Complete
                        </span>
                    </div>

                    {logoResult.type === 'blob' ||
                    logoResult.type === 'url' ||
                    logoResult.type === 'base64' ? (
                        <>
                            <div className={styles.logoDisplay}>
                                <img
                                    src={
                                        logoResult.type === 'blob'
                                            ? logoResult.objectUrl
                                            : logoResult.value
                                    }
                                    alt='Extracted logo'
                                    className={styles.logoImg}
                                    onError={(e) => {
                                        e.currentTarget.style.display = 'none';
                                    }}
                                />
                            </div>
                            <div className={styles.resultActions}>
                                <a
                                    href={
                                        logoResult.type === 'blob'
                                            ? logoResult.objectUrl
                                            : logoResult.value
                                    }
                                    download={
                                        logoResult.type === 'blob'
                                            ? `extracted-logo.${logoResult.extension || 'png'}`
                                            : 'extracted-logo.png'
                                    }
                                    target='_blank'
                                    rel='noreferrer'
                                    className={styles.downloadBtn}
                                >
                                    ⬇ Download Logo
                                </a>
                                <button type='button' className={styles.retryBtn} onClick={handleReset}>
                                    ↺ Try another image
                                </button>
                            </div>
                        </>
                    ) : (
                        <>
                            <pre className={styles.rawResponse}>{logoResult.value}</pre>
                            <div className={styles.resultActions}>
                                <button type='button' className={styles.retryBtn} onClick={handleReset} style={{ flex: 1 }}>
                                    ↺ Try another image
                                </button>
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default AiAgentLogo;
